import React from "react";

import { Box, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { colors } from "@zapalacorp/tokens";

import useStyles from "../../styles";

export interface InboxMockItem {
  id: string;
  sender: string;
  subject: string;
  snippet: string;
  time: string;
}

interface FullScreenEmailPortalPreviewProps {
  open: boolean;
  onClose: () => void;
  email: string;
  emailPreviewHtml: string;
  inboxMockItems: InboxMockItem[];
  tWithFallback: (key: string, fallback: string) => string;
}

const FullScreenEmailPortalPreview = ({
  open,
  onClose,
  email,
  emailPreviewHtml,
  inboxMockItems,
  tWithFallback,
}: FullScreenEmailPortalPreviewProps) => {
  const classes = useStyles();

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <Box className={classes.previewPanelFullScreen}>
        <Box
          style={{
            display: "flex",
            flex: 1,
            minHeight: 0,
            backgroundColor: colors.gray50,
          }}
        >
          <Box
            style={{
              width: "340px",
              minWidth: "340px",
              borderRight: `1px solid ${colors.gray200}`,
              backgroundColor: colors.white,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Typography
              style={{
                fontFamily: "Manrope",
                fontSize: "16px",
                fontWeight: 700,
                lineHeight: "24px",
                color: "#475467",
                padding: "16px 20px",
                borderBottom: `1px solid ${colors.gray200}`,
                margin: 0,
              }}
            >
              {tWithFallback("inbox", "Inbox")}
            </Typography>
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                padding: "12px",
                overflowY: "auto",
                minHeight: 0,
              }}
            >
              {inboxMockItems.map((item, index) => (
                <Box
                  key={item.id}
                  style={{
                    border: `1px solid ${index === 0 ? colors.brandBlue500 : colors.gray200}`,
                    borderRadius: "10px",
                    padding: "12px",
                    backgroundColor: index === 0 ? "#EFF8FF" : colors.white,
                  }}
                >
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                      marginBottom: "4px",
                    }}
                  >
                    <Typography
                      style={{
                        fontFamily: "Manrope",
                        fontSize: "13px",
                        fontWeight: 700,
                        lineHeight: "18px",
                        color: "#344054",
                      }}
                    >
                      {item.sender}
                    </Typography>
                    <Typography
                      style={{
                        fontFamily: "Manrope",
                        fontSize: "12px",
                        lineHeight: "16px",
                        color: "#98A2B3",
                      }}
                    >
                      {item.time}
                    </Typography>
                  </Box>
                  <Typography
                    style={{
                      fontFamily: "Manrope",
                      fontSize: "13px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      color: "#475467",
                      marginBottom: "2px",
                    }}
                  >
                    {item.subject}
                  </Typography>
                  <Typography
                    style={{
                      fontFamily: "Manrope",
                      fontSize: "12px",
                      lineHeight: "16px",
                      color: "#98A2B3",
                    }}
                  >
                    {item.snippet}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              backgroundColor: colors.white,
            }}
          >
            <Box className={classes.previewBrowserChromeFullScreen}>
              <Box className={classes.previewTrafficLights}>
                <Box className={classes.previewTrafficDot} sx={{ backgroundColor: "#f97066" }} />
                <Box className={classes.previewTrafficDot} sx={{ backgroundColor: "#fdb022" }} />
                <Box className={classes.previewTrafficDot} sx={{ backgroundColor: "#32d583" }} />
              </Box>
              <Box className={classes.previewUrl}>{email || "https://yourmail.com/"}</Box>
              <IconButton
                size="small"
                className={classes.previewChromeIcon}
                aria-label={tWithFallback("closePreview", "Close preview")}
                onClick={onClose}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Box>

            <Box className={classes.previewBodyShellFullScreen}>
              <div
                className={classes.previewHtml}
                dangerouslySetInnerHTML={{
                  __html: emailPreviewHtml,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default FullScreenEmailPortalPreview;
